import { useTranslation } from 'next-i18next'
import { useTheme } from 'styled-components'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { MileageAnalysisBadge } from '@/features/listing/components/mileage-analysis/MileageAnalysisBadge'
import { MileageRange } from '@/features/listing/constants/mileageAnalysis'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { BodyText } from '@/ui/atoms/body-text'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { ListItem } from '@/ui/atoms/list-item'
import { Spacing } from '@/ui/atoms/spacing'
import { List } from '@/ui/molecules/list'
import { Modal, ModalBody, ModalHeader } from '@/ui/molecules/modal/Modal'

export const MILEAGE_RANGE_LIST = [
  MileageRange.BelowAverage,
  MileageRange.Average,
  MileageRange.AboveAverage,
]

export const MileageAnalysisModal = () => {
  const { isOpen, handleCloseModal } = useModal(MODAL_REGISTRY.MILEAGE_ANALYSIS_LEARN)

  const { colors, spacing } = useTheme()
  const { t } = useTranslation(TRANSLATION_KEYS.LISTING)

  if (!isOpen) return null

  return (
    <Modal
      label={t('listing:mileage_analysis.modal.title')}
      id="mileage-analysis-modal"
      data-testid="mileage-analysis-modal"
      isOpen={isOpen}
      hasCloseButton
      onCancel={handleCloseModal}
      width={{ small: '100vw', medium: '68rem' }}
    >
      <ModalHeader>
        <Spacing mBottom={spacing.defaultSmall}>
          <HeadlineText as="h3" size="large">
            {t('listing:mileage_analysis.modal.title')}
          </HeadlineText>
        </Spacing>
      </ModalHeader>

      <ModalBody>
        <Spacing mBottom={spacing.default}>
          <BodyText>{t('listing:mileage_analysis.modal.description')}</BodyText>
        </Spacing>

        <Spacing mBottom={spacing.xLarge}>
          <Flex gap={spacing.large} flexDirection="column">
            <HeadlineText as="h4" size="medium">
              {t('listing:mileage_analysis.modal.badge_header')}
            </HeadlineText>

            {MILEAGE_RANGE_LIST.map((badgeRange) => {
              return (
                <Flex
                  key={`mileage-learn-more-${badgeRange}`}
                  gap={spacing.default}
                  flexDirection="column"
                >
                  <MileageAnalysisBadge range={badgeRange} />
                  <BodyText>
                    {t(`listing:mileage_analysis.modal.${badgeRange}_definition`)}
                  </BodyText>
                </Flex>
              )
            })}
          </Flex>
        </Spacing>

        <Spacing mBottom={spacing.large}>
          <HeadlineText as="h4" size="medium">
            {t('listing:mileage_analysis.modal.km_per_year_header')}
          </HeadlineText>
        </Spacing>

        <Flex flexDirection="column" gap={spacing.default}>
          <BodyText>{t('listing:mileage_analysis.modal.km_per_year_description')}</BodyText>

          <List>
            <ListItem variant="bullet" bulletColor={colors.grey.primary}>
              <BodyText as="span" weight="medium">
                (I)
              </BodyText>{' '}
              <BodyText as="span">
                {t('listing:mileage_analysis.modal.km_per_year_bullet1')}
              </BodyText>
            </ListItem>
            <ListItem variant="bullet" bulletColor={colors.grey.primary}>
              <BodyText as="span">
                {t('listing:mileage_analysis.modal.km_per_year_bullet2a')}
              </BodyText>{' '}
              <BodyText as="span" weight="medium">
                (II)
              </BodyText>{' '}
              <BodyText as="span">
                {t('listing:mileage_analysis.modal.km_per_year_bullet2b')}
              </BodyText>
            </ListItem>
          </List>
        </Flex>
      </ModalBody>
    </Modal>
  )
}
